import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createSelector } from '@reduxjs/toolkit';

import { getContractRequirementsAll, getContractRequirementsUnmet } from '@/store/slices/contractSlice';
import { getWeeksArray } from '@/store/slices/utilsSlice';
import { useAppDispatch, useAppSelector, usePagination } from '@/hooks';
import { OFFSET_MINUS, OFFSET_PLUS } from '@/config/variables';
import { RequirementsMet, RequirementsMetIcon, StyledPagination } from './ContractsRequirements.styled';
import { ContentTitle, NoData } from '@/components/agent/dashboard/common';
import { NoContractReqs } from '@/assets/icons';
import {
  selectContractRequirementsAll,
  selectContractRequirementsUnmet,
  selectIsPendingAction,
} from '@/store/selectors/contractSelectors';
import { ContractsRequirementsSkeleton } from './ContractsRequirements.skeleton';
import { selectCurrentWeek, selectIsMobileScreenSize } from '@/store/selectors/utilsSelectors';
import { CONTRACTS_REQUIREMENTS_PAGE_SIZE } from '@/constants';
import { ContractsRequirementsDesktopTable } from './ContractsRequirementsDesktopTable';
import { ContractsRequirementsMobileTable } from './ContractsRequirementsMobileTable';
import { ContractsRequirementsTitle } from './ContractsRequirementsTitle';

const contractsRequirementsSelector = createSelector(
  selectCurrentWeek,
  selectIsMobileScreenSize,
  selectContractRequirementsAll,
  selectContractRequirementsUnmet,
  selectIsPendingAction(getContractRequirementsAll.typePrefix),
  selectIsPendingAction(getContractRequirementsUnmet.typePrefix),
  (
    currentWeek,
    isMobileScreenSize,
    contractRequirementsAll,
    contractRequirementsUnmet,
    isGetContractRequirementsAllPending,
    isGetContractRequirementsUnmetPending,
  ) => ({
    currentWeek,
    isMobileScreenSize,
    contractRequirementsAll,
    contractRequirementsUnmet,
    isReady: !isGetContractRequirementsAllPending && !isGetContractRequirementsUnmetPending,
  }),
);

const ContractsRequirements = (): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    currentWeek,
    isMobileScreenSize,
    contractRequirementsUnmet: contractsUnmet,
    contractRequirementsAll: contractsAll,
    isReady,
  } = useAppSelector(contractsRequirementsSelector);

  const { handlePrev, handleNext, page, paginatedData, paginationOptions } = usePagination({
    data: contractsUnmet,
    pageSize: CONTRACTS_REQUIREMENTS_PAGE_SIZE,
  });

  useEffect(() => {
    const getContractRequirementsAllCall = dispatch(getContractRequirementsAll());
    return () => getContractRequirementsAllCall.abort();
  }, [dispatch]);

  useEffect(() => {
    if (currentWeek === 0) return;

    const getContractRequirementsUnmetCall = dispatch(
      getContractRequirementsUnmet({ startWeekId: currentWeek, endWeekId: currentWeek + 1 }),
    );
    return () => getContractRequirementsUnmetCall.abort();
  }, [currentWeek, dispatch]);

  useEffect(() => {
    if (currentWeek === 0) return;

    dispatch(
      getWeeksArray({
        weekId: currentWeek,
        offsetMinus: OFFSET_MINUS,
        offsetPlus: OFFSET_PLUS,
      }),
    );
  }, [currentWeek, dispatch]);

  if (!isReady) {
    return <ContractsRequirementsSkeleton />;
  }

  if (!contractsAll?.length && !contractsUnmet.length) {
    return (
      <>
        <ContentTitle>{t`dashboard.gigRequirements.textRequirements`}</ContentTitle>
        <NoData icon={NoContractReqs} text={t`dashboard.gigRequirements.textNoRequirements`} />
      </>
    );
  }

  if (contractsAll?.length && !contractsUnmet.length) {
    return (
      <>
        <ContentTitle>{t`dashboard.gigRequirements.textRequirements`}</ContentTitle>
        <RequirementsMet>
          <RequirementsMetIcon />
          {t`dashboard.gigRequirements.textAllRequirementsMet`}
        </RequirementsMet>
      </>
    );
  }

  return (
    <>
      <ContractsRequirementsTitle contractsUnmetLength={contractsUnmet.length} />
      {isMobileScreenSize ? (
        <ContractsRequirementsMobileTable paginatedData={paginatedData} page={page} />
      ) : (
        <ContractsRequirementsDesktopTable paginatedData={paginatedData} page={page} />
      )}
      <StyledPagination
        page={page}
        pageCount={paginatedData.length - 1}
        options={[paginationOptions]}
        defaultValue={0}
        onLeftClick={handlePrev}
        onRightClick={handleNext}
      />
    </>
  );
};

export { ContractsRequirements };
